import { ChartService } from '../services/chartService'
import type { CreateChart, ChartDataset, ChartConfiguration } from '@shared/schemas/chart'

// ChartConfig interface from ChartConfigurationDialog
interface ChartConfig {
  chartType: 'bar' | 'line' | 'pie'
  xAxis: string
  yAxis: string
  groupBy?: string
  title: string
  colorPalette?: string
  chartData?: any[]
  isMultiSeries?: boolean
  seriesInfo?: {
    keys: string[]
    colors: string[]
  }
}

/**
 * Converts ChartConfig from ChartConfigurationDialog to CreateChart format for database storage
 */
export function convertChartConfigToCreateChart(
  config: ChartConfig,
  data: Record<string, any>[],
  organizationId: string,
  userId: string
): CreateChart {
  // Convert data to ChartDataset format
  const datasets: ChartDataset[] = []
  
  if (config.isMultiSeries && config.seriesInfo && config.chartData) {
    // For multi-series charts, create datasets for each series
    const seriesData = config.chartData
    const seriesKeys = config.seriesInfo.keys
    const seriesColors = config.seriesInfo.colors
    
    seriesKeys.forEach((seriesKey, index) => {
      const dataPoints = seriesData.map(point => ({
        label: String(point.name || ''),
        value: Number(point[seriesKey] || 0),
        color: seriesColors[index % seriesColors.length]
      }))
      
      datasets.push({
        label: seriesKey,
        data: dataPoints,
        backgroundColor: [seriesColors[index % seriesColors.length]],
        borderColor: [seriesColors[index % seriesColors.length]],
        borderWidth: 1
      })
    })
  } else {
    // For single series charts
    const dataPoints = data.map(row => ({
      label: String(row[config.xAxis] || ''),
      value: Number(row[config.yAxis] || 0),
      color: undefined
    }))
    
    datasets.push({
      label: config.title,
      data: dataPoints,
      backgroundColor: config.seriesInfo?.colors || undefined,
      borderColor: config.seriesInfo?.colors || undefined,
      borderWidth: 1
    })
  }
  
  // Create configuration with color palette support
  const configuration: Partial<ChartConfiguration> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: config.isMultiSeries || false,
        position: 'top'
      },
      title: {
        display: true,
        text: config.title
      }
    },
    // Include the color palette in configuration
    colorPalette: config.colorPalette
  }
  
  return {
    organizationId,
    userId,
    title: config.title,
    description: `${config.chartType} chart showing ${config.yAxis} by ${config.xAxis}${config.groupBy ? ` (grouped by ${config.groupBy})` : ''}`,
    type: config.chartType,
    datasets,
    configuration,
    tags: [],
    isPublic: false
  }
}

/**
 * Saves a chart from ChartConfigurationDialog to the database
 */
export async function saveChartFromConfig(
  config: ChartConfig,
  data: Record<string, any>[],
  organizationId: string,
  userId: string
): Promise<void> {
  const createChartData = convertChartConfigToCreateChart(config, data, organizationId, userId)
  return await ChartService.create(createChartData)
}